import { FC, useContext, useMemo, useState } from "react";

import { useTime } from "@/app/application/TimeContext";
import {
	GetLanguageValueAsString,
	Translate,
} from "@/app/application/Translate";
import { TranslationContext } from "@/app/providers";
import { Language } from "@/model/Language/Language";
import { CloseActionButton } from "@/shared/ui";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import UpdateIcon from "@mui/icons-material/Update";
import {
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	LinearProgress,
	List,
	ListItem,
	Skeleton,
	Stack,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import timezonePlugin from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import * as echarts from "echarts";
import ReactECharts from "echarts-for-react";

import { insertDomain } from "@/service/ApplicationQueries";
import { useFetchArchivedProcedures } from "@/service/ArchiveQueries";

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

export interface IActiveArchiveProcedures {
	startDate?: Date;
	endDate?: Date;
	selectedUser?: number;
	expanded?: boolean;
}

type ArchiveUserLite = {
	UserId: number;
	UserName?: string;
	UserFullName?: string;
	UserEmail?: string;
};

type ArchiveUpdateLite = {
	User?: ArchiveUserLite;
	Date?: string;
	Status?: number;
	CheckListId?: number;
	CheckListName?: string;
};

type ArchiveProjectLite = {
	Id: number;
	Name: string;
	Description?: string;
	Created?: string;
	Updated?: string;
	ProjectType?: string;
	FileCreated?: boolean;
	Link?: string;
	Users?: ArchiveUserLite[];
	Updates?: ArchiveUpdateLite[];
};

const summaryCardSx = {
	p: 1.25,
	border: 1,
	borderColor: "divider",
	borderRadius: 1.5,
	bgcolor: "background.paper",
};

const toSortedUpdates = (updates: ArchiveUpdateLite[] = []) =>
	[...updates].sort(
		(a, b) => new Date(a.Date ?? 0).getTime() - new Date(b.Date ?? 0).getTime(),
	);

const formatDateTime = (value: string | undefined, timezone: string) => {
	if (!value) return "-";
	return dayjs.utc(value).tz(timezone).format("DD/MM/YY HH:mm:ss");
};

const checklistLabel = (update: ArchiveUpdateLite) => {
	const name = update.CheckListName?.trim();
	if (name) return name;
	if (update.CheckListId) return `Checklist #${update.CheckListId}`;
	return "Unspecified checklist";
};

const getLastActivityForItem = (
	item: ArchiveProjectLite | null,
	updates: ArchiveUpdateLite[],
	timezone: string,
) => {
	if (!item) return null;

	const timestamps: number[] = [];
	if (item.Created) timestamps.push(new Date(item.Created).getTime());
	if (item.Updated) timestamps.push(new Date(item.Updated).getTime());
	updates.forEach((update) => {
		if (update.Date) timestamps.push(new Date(update.Date).getTime());
	});

	if (timestamps.length === 0) return null;

	return dayjs
		.utc(Math.max(...timestamps))
		.tz(timezone)
		.format("DD/MM/YY HH:mm:ss");
};

export const ActiveArchiveProcedures: FC<IActiveArchiveProcedures> = (
	props,
) => {
	const translation: Language | null = useContext(TranslationContext);
	const options = {
		StartDate: props.startDate ? dayjs(props.startDate).toDate() : undefined,
		EndDate: props.endDate
			? dayjs(props.endDate).startOf("day").add(1, "day").toDate()
			: undefined,
		SelectedUser: props.selectedUser ?? 0,
	};

	const { isLoading, data: archivedProcedures } =
		useFetchArchivedProcedures(options);
	const { timezone } = useTime();
	const theme = useTheme();

	const [open, setOpen] = useState(false);
	const [selectedItem, setSelectedItem] = useState<ArchiveProjectLite | null>(
		null,
	);

	const allProjects = (archivedProcedures?.ArchivedProjects ??
		[]) as ArchiveProjectLite[];

	const filteredProcedures = useMemo(() => {
		if ((props.selectedUser ?? 0) === 0) {
			return allProjects;
		}

		return allProjects.filter((proc) =>
			(proc.Users ?? []).some((user) => user.UserId === props.selectedUser),
		);
	}, [allProjects, props.selectedUser]);

	const selectedUpdates = useMemo(
		() => toSortedUpdates(selectedItem?.Updates ?? []),
		[selectedItem],
	);
	const selectedUsers = selectedItem?.Users ?? [];
	const selectedDownloadPath =
		selectedItem?.FileCreated && selectedItem?.Link
			? insertDomain(selectedItem.Link, {
					insertIntoHtmlElement: false,
				})
			: undefined;

	const lastActivity = useMemo(
		() => getLastActivityForItem(selectedItem, selectedUpdates, timezone),
		[selectedItem, selectedUpdates, timezone],
	);

	const selectedActiveCount = selectedUpdates.filter(
		(update) => update.Status === 1,
	).length;
	const selectedInactiveCount = selectedUpdates.length - selectedActiveCount;

	const checklistFlowRows = useMemo(() => {
		const byChecklist = new Map<
			string,
			{
				checklist: string;
				active: number;
				inactive: number;
				minutes: number;
				transitions: number;
			}
		>();

		selectedUpdates.forEach((update, index) => {
			const key = checklistLabel(update);
			const current = byChecklist.get(key) ?? {
				checklist: key,
				active: 0,
				inactive: 0,
				minutes: 0,
				transitions: 0,
			};

			if (update.Status === 1) {
				current.active += 1;
			} else {
				current.inactive += 1;
			}

			if (index > 0) {
				const previous = selectedUpdates[index - 1];
				if (previous.Date && update.Date) {
					const minutes =
						(new Date(update.Date).getTime() -
							new Date(previous.Date).getTime()) /
						60000;
					if (Number.isFinite(minutes) && minutes >= 0) {
						current.minutes += minutes;
						current.transitions += 1;
					}
				}
			}

			byChecklist.set(key, current);
		});

		return Array.from(byChecklist.values())
			.sort((a, b) => b.active + b.inactive - (a.active + a.inactive))
			.slice(0, 12);
	}, [selectedUpdates]);

	const cadenceRows = useMemo(() => {
		if (selectedUpdates.length < 2) return [];
		return selectedUpdates
			.slice(1)
			.map((update, index) => {
				const previous = selectedUpdates[index];
				const minutes =
					(previous.Date && update.Date
						? (new Date(update.Date).getTime() -
								new Date(previous.Date).getTime()) /
							60000
						: 0) || 0;

				return {
					label: `U${index + 2}`,
					minutes: Math.max(0, Math.round(minutes * 10) / 10),
				};
			})
			.slice(-30);
	}, [selectedUpdates]);

	const cadenceStats = useMemo(() => {
		if (cadenceRows.length === 0) return null;
		const values = cadenceRows
			.map((row) => row.minutes)
			.filter((value) => value >= 0);
		if (values.length === 0) return null;

		const total = values.reduce((sum, value) => sum + value, 0);
		return {
			avg: Math.round((total / values.length) * 10) / 10,
			min: Math.round(Math.min(...values) * 10) / 10,
			max: Math.round(Math.max(...values) * 10) / 10,
		};
	}, [cadenceRows]);
	const tooltipMutedTextColor =
		theme.palette.mode === "dark"
			? "rgba(226,232,240,0.72)"
			: "rgba(71,85,105,0.9)";
	const tooltipShellSx = useMemo(
		() => ({
			renderMode: "html" as const,
			appendToBody: true,
			confine: false,
			enterable: true,
			transitionDuration: 0.08,
			backgroundColor:
				theme.palette.mode === "dark"
					? "rgba(15, 23, 42, 0.96)"
					: "rgba(255, 255, 255, 0.98)",
			borderColor: alpha(
				theme.palette.primary.main,
				theme.palette.mode === "dark" ? 0.45 : 0.22,
			),
			borderWidth: 1,
			borderRadius: 10,
			padding: [10, 12],
			textStyle: {
				color: theme.palette.text.primary,
				fontSize: 12,
			},
			extraCssText:
				"box-shadow: 0 10px 28px rgba(2,6,23,0.26); max-width: 360px; white-space: normal; z-index: 9999; backdrop-filter: blur(2px);",
		}),
		[theme],
	);
	const cadenceChartOption = useMemo<echarts.EChartsOption>(() => {
		const labels = cadenceRows.map((row) => row.label);
		const values = cadenceRows.map((row) => Number(row.minutes ?? 0));
		const lineAccent = theme.palette.primary.main;

		return {
			backgroundColor: "transparent",
			grid: { left: 10, right: 8, top: 8, bottom: 8 },
			xAxis: {
				type: "category",
				data: labels,
				boundaryGap: false,
				axisLabel: { show: false },
				axisTick: { show: false },
				axisLine: { show: false },
			},
			yAxis: {
				type: "value",
				axisLabel: { show: false },
				axisTick: { show: false },
				axisLine: { show: false },
				splitLine: { show: false },
			},
			tooltip: {
				...tooltipShellSx,
				trigger: "axis",
				axisPointer: { type: "line" },
				formatter: (params: unknown) => {
					const point = (Array.isArray(params) ? params[0] : params) as {
						dataIndex?: number;
						data?: number;
						marker?: string;
					};
					const idx = Number(point?.dataIndex ?? 0);
					const label = labels[idx] ?? `U${idx + 1}`;
					const value = Number(point?.data ?? values[idx] ?? 0);
					return `
						<div style="max-width:300px">
							<div style="font-weight:700; margin-bottom:6px">${label}</div>
							<div style="display:flex; justify-content:space-between; gap:12px;">
								<span>${point?.marker ?? ""}Cadence gap</span>
								<strong>${value} min</strong>
							</div>
							<div style="margin-top:6px; color:${tooltipMutedTextColor}; font-size:12px;">
								Time between consecutive updates
							</div>
						</div>
					`;
				},
			},
			series: [
				{
					type: "line",
					name: "Cadence gap",
					data: values,
					smooth: true,
					symbol: "circle",
					symbolSize: 5,
					lineStyle: { width: 2, color: lineAccent },
					itemStyle: { color: lineAccent },
					areaStyle: {
						opacity: theme.palette.mode === "dark" ? 0.28 : 0.14,
						color: lineAccent,
					},
				},
			],
		};
	}, [cadenceRows, theme, tooltipMutedTextColor, tooltipShellSx]);

	const handleClickOpen = (item: ArchiveProjectLite) => {
		setSelectedItem(item);
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
		setSelectedItem(null);
	};

	if (isLoading) {
		return (
			<>
				<Skeleton variant="rounded" width="100%" height={60} />
				<br />
				<Skeleton variant="rounded" width="100%" height={60} />
			</>
		);
	}

	return (
		<Box
			sx={{
				mt: 2,
				p: { xs: 1.25, sm: 2 },
				bgcolor: "background.default",
				border: 1,
				borderColor: "divider",
				borderRadius: 2,
			}}
		>
			<Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700 }}>
				<Translate String="Archive.History.Header">History</Translate>
			</Typography>

			{filteredProcedures.length === 0 ? (
				<Box
					sx={{
						p: 2,
						bgcolor: "background.paper",
						borderRadius: 2,
						border: 1,
						borderColor: "divider",
					}}
				>
					<Typography variant="body2" color="text.secondary">
						<Translate String="Archive.History.Empty">
							No archived runs found for the selected filters.
						</Translate>
					</Typography>
				</Box>
			) : null}

			<List>
				{filteredProcedures.map((item) => {
					const itemUpdates = toSortedUpdates(item.Updates ?? []);
					const created = formatDateTime(item.Created, timezone);
					const latest =
						itemUpdates.length > 0
							? formatDateTime(
									itemUpdates[itemUpdates.length - 1].Date,
									timezone,
								)
							: created;
					const itemDownloadPath =
						item.FileCreated && item.Link
							? insertDomain(item.Link, { insertIntoHtmlElement: false })
							: undefined;

					return (
						<ListItem
							key={item.Id}
							sx={{
								display: "flex",
								flexDirection: "column",
								p: 2,
								bgcolor: "background.paper",
								border: 1,
								borderColor: "divider",
								borderRadius: 2,
								mb: 1.25,
								cursor: "pointer",
								alignItems: "stretch",
							}}
							onClick={() => handleClickOpen(item)}
						>
							<Stack spacing={1.25} sx={{ width: "100%" }}>
								<Stack
									direction={{ xs: "column", sm: "row" }}
									justifyContent="space-between"
									alignItems={{ xs: "flex-start", sm: "center" }}
									spacing={1}
								>
									<Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
										{item.Name}
									</Typography>
									<Chip
										size="small"
										color="primary"
										variant="outlined"
										label={`${itemUpdates.length} updates`}
									/>
								</Stack>

									<Typography variant="body2" color="text.secondary">
										{item.Description || (
											<Translate String="Common.NoDescription">
												No description
											</Translate>
										)}
									</Typography>

									<Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
										<Typography variant="caption" color="text.secondary">
											<Translate String="Common.Created">Created</Translate>:{" "}
											{created}
										</Typography>
										<Typography variant="caption" color="text.secondary">
											<Translate String="Common.LastActivity">
												Last activity
											</Translate>
											: {latest}
										</Typography>
										<Typography variant="caption" color="text.secondary">
											<Translate String="Common.Users">Users</Translate>:{" "}
											{item.Users?.length ?? 0}
										</Typography>
									</Stack>

								<Stack direction="row" spacing={1} sx={{ pt: 0.5 }}>
									<Button
										size="small"
										variant="contained"
										color="info"
										startIcon={<InfoOutlinedIcon />}
										onClick={(event) => {
											event.stopPropagation();
											handleClickOpen(item);
										}}
									>
										<Translate String="Archive.File.Preview">Details</Translate>
									</Button>
									{itemDownloadPath ? (
										<Button
											size="small"
											variant="outlined"
											color="info"
											startIcon={<DownloadIcon />}
											href={itemDownloadPath}
											download
											onClick={(event) => event.stopPropagation()}
										>
											<Translate String="Archive.File.Download">
												Download
											</Translate>
										</Button>
									) : null}
								</Stack>
							</Stack>
						</ListItem>
					);
				})}
			</List>

			{selectedItem ? (
					<Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
						<DialogTitle>
							{selectedItem.Name}
							<IconButton
								aria-label={GetLanguageValueAsString(
									translation,
									"Common.Close",
									"Close",
								)}
								onClick={handleClose}
								size="small"
							>
								<CloseIcon fontSize="small" />
							</IconButton>
						</DialogTitle>
					<DialogContent dividers>
						<Stack spacing={2}>
							<Box
								sx={{
									p: 2,
									bgcolor: "background.default",
									border: 1,
									borderColor: "divider",
									borderRadius: 2,
								}}
								>
									<Typography variant="subtitle2" sx={{ mb: 0.5 }}>
										<Translate String="Common.Description">
											Description
										</Translate>
									</Typography>
									<Typography variant="body2" color="text.secondary">
										{selectedItem.Description || (
											<Translate String="Common.NoDescriptionProvided">
												No description provided.
											</Translate>
										)}
									</Typography>
								</Box>

							<Box
								sx={{
									display: "grid",
									gridTemplateColumns: {
										xs: "1fr",
										sm: "repeat(2, minmax(0, 1fr))",
										md: "repeat(3, minmax(0, 1fr))",
									},
									gap: 1,
								}}
							>
								<Box sx={summaryCardSx}>
										<Stack direction="row" spacing={0.75} alignItems="center">
											<AccessTimeIcon sx={{ fontSize: 16 }} />
											<Typography variant="caption" color="text.secondary">
												<Translate String="Common.Created">
													Created
												</Translate>
											</Typography>
										</Stack>
									<Typography variant="body2">
										{formatDateTime(selectedItem.Created, timezone)}
									</Typography>
								</Box>
								<Box sx={summaryCardSx}>
										<Stack direction="row" spacing={0.75} alignItems="center">
											<UpdateIcon sx={{ fontSize: 16 }} />
											<Typography variant="caption" color="text.secondary">
												<Translate String="Common.LastActivity">
													Last activity
												</Translate>
											</Typography>
										</Stack>
									<Typography variant="body2">{lastActivity ?? "-"}</Typography>
								</Box>
									<Box sx={summaryCardSx}>
										<Typography variant="caption" color="text.secondary">
											<Translate String="Common.ProjectType">
												Project type
											</Translate>
										</Typography>
									<Typography variant="body2">
										{selectedItem.ProjectType ?? "-"}
									</Typography>
								</Box>
									<Box sx={summaryCardSx}>
										<Typography variant="caption" color="text.secondary">
											<Translate String="Common.TotalDuration">
												Total duration
											</Translate>
										</Typography>
									<Typography variant="body2">
										{selectedUpdates.length > 1
											? calculateDuration([...selectedUpdates])
											: "N/A"}
									</Typography>
								</Box>
									<Box sx={summaryCardSx}>
										<Typography variant="caption" color="text.secondary">
											<Translate String="Common.Updates">Updates</Translate>
										</Typography>
									<Typography variant="body2">
										{selectedUpdates.length}
									</Typography>
								</Box>
									<Box sx={summaryCardSx}>
										<Typography variant="caption" color="text.secondary">
											<Translate String="Archive.StatusSplit">
												Status split
											</Translate>
										</Typography>
										<Typography variant="body2">
											<Translate String="Common.Active">Active</Translate>{" "}
											{selectedActiveCount} /{" "}
											<Translate String="Common.Inactive">Inactive</Translate>{" "}
											{selectedInactiveCount}
										</Typography>
									</Box>
							</Box>

							<Box
								sx={{
									p: 2,
									bgcolor: "background.default",
									border: 1,
									borderColor: "divider",
									borderRadius: 2,
								}}
								>
									<Typography variant="subtitle2" sx={{ mb: 1 }}>
										<Translate String="Common.Participants">
											Participants
										</Translate>
									</Typography>
								<Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
										{selectedUsers.length === 0 ? (
											<Typography variant="body2" color="text.secondary">
												<Translate String="Common.NoUsersAvailable">
													No users available.
												</Translate>
											</Typography>
									) : (
										selectedUsers.map((user) => (
											<Chip
												key={user.UserId}
												size="small"
												label={
													user.UserFullName ||
													user.UserName ||
													`User ${user.UserId}`
												}
											/>
										))
									)}
								</Stack>
							</Box>

							{selectedUpdates.length > 0 ? (
								<Box
									sx={{
										p: 2,
										bgcolor: "background.default",
										border: 1,
										borderColor: "divider",
										borderRadius: 2,
									}}
									>
										<Typography variant="subtitle2" sx={{ mb: 1 }}>
											<Translate String="Archive.UpdateFlow">
												Update flow
											</Translate>
										</Typography>
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{ display: "block", mb: 1.5 }}
										>
											<Translate String="Archive.UpdateFlow.Description">
												Checklist activity and timing between updates.
											</Translate>
										</Typography>

									{cadenceStats ? (
										<Stack
											direction={{ xs: "column", sm: "row" }}
											spacing={1}
											sx={{ mb: 1.5 }}
										>
											<Chip
												size="small"
												variant="outlined"
												label={`Avg gap: ${cadenceStats.avg} min`}
											/>
											<Chip
												size="small"
												variant="outlined"
												label={`Min gap: ${cadenceStats.min} min`}
											/>
											<Chip
												size="small"
												variant="outlined"
												label={`Max gap: ${cadenceStats.max} min`}
											/>
										</Stack>
									) : null}

									{cadenceRows.length > 0 ? (
										<Box sx={{ width: "100%", mb: 1.5 }}>
												<ReactECharts
												notMerge
												opts={{ renderer: "canvas" }}
												option={cadenceChartOption}
												style={{ height: 64, width: "100%" }}
											/>
												<Typography variant="caption" color="text.secondary">
													<Translate String="Archive.CadenceTrend.Description">
														Cadence trend across latest updates (rightmost is
														newest).
													</Translate>
												</Typography>
										</Box>
									) : null}

									{checklistFlowRows.length > 0 ? (
										<Stack spacing={1}>
											{checklistFlowRows.slice(0, 6).map((row) => {
												const total = row.active + row.inactive;
												const activePercent =
													total > 0 ? (row.active / total) * 100 : 0;
												const avgMinutes =
													row.transitions > 0
														? Math.round((row.minutes / row.transitions) * 10) /
															10
														: 0;
												return (
													<Box
														key={row.checklist}
														sx={{
															p: 1.25,
															border: 1,
															borderColor: "divider",
															borderRadius: 1.5,
															bgcolor: alpha(
																theme.palette.background.paper,
																0.88,
															),
														}}
													>
														<Stack
															direction="row"
															justifyContent="space-between"
															alignItems="center"
															spacing={1}
														>
															<Typography
																variant="body2"
																sx={{ fontWeight: 600 }}
																noWrap
																title={row.checklist}
															>
																{row.checklist}
															</Typography>
																<Typography
																	variant="caption"
																	color="text.secondary"
																>
																	{total}{" "}
																	<Translate String="Common.Updates">
																		updates
																	</Translate>
																</Typography>
														</Stack>
														<LinearProgress
															variant="determinate"
															value={activePercent}
															color="success"
															sx={{
																mt: 0.75,
																height: 8,
																borderRadius: 999,
																bgcolor: alpha(
																	theme.palette.text.primary,
																	0.12,
																),
															}}
														/>
														<Stack
															direction="row"
															justifyContent="space-between"
															sx={{ mt: 0.5 }}
														>
																<Typography
																	variant="caption"
																	color="text.secondary"
																>
																	<Translate String="Common.Active">
																		Active
																	</Translate>{" "}
																	{row.active} /{" "}
																	<Translate String="Common.Inactive">
																		Inactive
																	</Translate>{" "}
																	{row.inactive}
																</Typography>
																<Typography
																	variant="caption"
																	color="text.secondary"
																>
																	<Translate String="Archive.AvgGap">
																		Avg gap
																	</Translate>{" "}
																	{avgMinutes}{" "}
																	<Translate String="Common.MinutesShort">
																		min
																	</Translate>
																</Typography>
														</Stack>
													</Box>
												);
											})}
										</Stack>
										) : (
											<Typography variant="body2" color="text.secondary">
												<Translate String="Archive.UpdateFlow.Minimum">
													Need at least 2 updates to calculate compact flow
													insights.
												</Translate>
											</Typography>
										)}
									</Box>
								) : null}

								<Box>
									<Typography variant="subtitle2" sx={{ mb: 1 }}>
										<Translate String="Archive.UpdateTimeline">
											Update timeline
										</Translate>
									</Typography>
								<List sx={{ p: 0 }}>
									{selectedUpdates.length === 0 ? (
										<Box
											sx={{
												p: 2,
												border: 1,
												borderColor: "divider",
												borderRadius: 2,
												bgcolor: "background.default",
											}}
											>
												<Typography variant="body2" color="text.secondary">
													<Translate String="Archive.NoUpdatesRecorded">
														No updates recorded.
													</Translate>
												</Typography>
										</Box>
									) : (
										selectedUpdates.map((update, index) => (
											<ListItem
												key={`${update.Date}-${index}`}
												sx={{
													display: "flex",
													flexDirection: "column",
													alignItems: "stretch",
													gap: 1,
													p: 1.5,
													mb: 1,
													bgcolor: "background.default",
													border: 1,
													borderColor: "divider",
													borderRadius: 2,
												}}
											>
												<Stack
													direction={{ xs: "column", sm: "row" }}
													justifyContent="space-between"
													alignItems={{ xs: "flex-start", sm: "center" }}
													spacing={1}
												>
													<Stack
														direction="row"
														spacing={1}
														alignItems="center"
													>
														<Chip
															size="small"
															color={
																update.Status === 1 ? "success" : "default"
															}
																label={
																	update.Status === 1
																		? GetLanguageValueAsString(
																				translation,
																				"Common.Active",
																				"Active",
																		  )
																		: GetLanguageValueAsString(
																				translation,
																				"Common.Inactive",
																				"Inactive",
																		  )
																}
															/>
														<Typography
															variant="body2"
															sx={{ fontWeight: 600 }}
															>
																{update.User?.UserFullName ||
																	update.User?.UserName ||
																	GetLanguageValueAsString(
																		translation,
																		"Common.UnknownUser",
																		"Unknown user",
																	)}
															</Typography>
													</Stack>
													<Typography variant="caption" color="text.secondary">
														{formatDateTime(update.Date, timezone)}
													</Typography>
												</Stack>

												<Stack
													direction={{ xs: "column", sm: "row" }}
													spacing={1.5}
													alignItems={{ xs: "flex-start", sm: "center" }}
												>
														<Typography variant="caption" color="text.secondary">
															<Translate String="Common.Checklist">
																Checklist
															</Translate>
															:{" "}
															{update.CheckListName ||
																(update.CheckListId
																	? `#${update.CheckListId}`
																	: GetLanguageValueAsString(
																			translation,
																			"Common.NotAvailableShort",
																			"N/A",
																	  ))}
														</Typography>
														{index > 0 ? (
															<Typography
																variant="caption"
																color="text.secondary"
															>
																<Translate String="Archive.SincePrevious">
																	Since previous
																</Translate>
																:{" "}
																{calculateUpdateDuration(
																	selectedUpdates[index - 1].Date ?? "",
																	update.Date ?? "",
															)}
														</Typography>
													) : null}
												</Stack>
											</ListItem>
										))
									)}
								</List>
							</Box>
						</Stack>
					</DialogContent>
					<DialogActions>
						{selectedDownloadPath ? (
							<Button
								variant="outlined"
								color="info"
								href={selectedDownloadPath}
								download
								startIcon={<DownloadIcon />}
							>
								<Translate String="Archive.File.Download">Download</Translate>
							</Button>
						) : null}
						<CloseActionButton onClick={handleClose}>
							<Translate String="Procedure.Button.Close">Close</Translate>
						</CloseActionButton>
					</DialogActions>
				</Dialog>
			) : null}
		</Box>
	);
};

const calculateDuration = (updates: ArchiveUpdateLite[]) => {
	if (updates.length < 2) return "N/A";

	const sortedUpdates = [...updates].sort(
		(a, b) => new Date(a.Date ?? 0).getTime() - new Date(b.Date ?? 0).getTime(),
	);

	const startDate = dayjs(sortedUpdates[0].Date);
	const endDate = dayjs(sortedUpdates[sortedUpdates.length - 1].Date);

	const days = endDate.diff(startDate, "day");
	const hours = endDate.diff(startDate, "hour") % 24;
	const minutes = endDate.diff(startDate, "minute") % 60;

	let formattedDuration = "";
	if (days > 0) formattedDuration += `${days} day${days > 1 ? "s" : ""} `;
	if (hours > 0) formattedDuration += `${hours} hour${hours > 1 ? "s" : ""} `;
	if (minutes > 0)
		formattedDuration += `${minutes} minute${minutes > 1 ? "s" : ""} `;

	return formattedDuration.trim() || "Less than a minute";
};

export const calculateUpdateDuration = (
	previousDate: string,
	currentDate: string,
) => {
	const start = dayjs(previousDate);
	const end = dayjs(currentDate);

	const days = end.diff(start, "day");
	const hours = end.diff(start, "hour") % 24;
	const minutes = end.diff(start, "minute") % 60;

	let durationString = "";
	if (days > 0) durationString += `${days} day${days > 1 ? "s" : ""} `;
	if (hours > 0) durationString += `${hours} hour${hours > 1 ? "s" : ""} `;
	if (minutes > 0)
		durationString += `${minutes} minute${minutes > 1 ? "s" : ""} `;

	return durationString.trim() || "Less than a minute";
};
